import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Site } from '../types';

interface AustraliaMapProps {
  sites: Site[];
}

// Australia center coordinates - adjusted for better view of populated areas
const AUSTRALIA_CENTER: [number, number] = [-30.0, 145.0];
const DEFAULT_ZOOM = 5;

// OpenStreetMap's standard tiles: free and keyless (CARTO's basemaps now need
// an API key). There is no dark variant, so dark mode is a CSS filter over the
// same tiles - see `.site-map` in index.css.
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_MAX_ZOOM = 19;

const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Custom marker icons
const createMarkerIcon = (color: string, isActive: boolean, isDark: boolean) => {
  const size = isActive ? 12 : 10;
  const strokeColor = isDark ? '#1f2937' : 'white';
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size * 2}" height="${size * 2}">
      <circle cx="12" cy="12" r="10" fill="${color}" stroke="${strokeColor}" stroke-width="2"/>
      ${isActive ? `<circle cx="12" cy="12" r="14" fill="${color}" fill-opacity="0.3"/>` : ''}
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [size * 2, size * 2],
    iconAnchor: [size, size],
    popupAnchor: [0, -size],
  });
};

// Hook to detect dark mode
function useDarkMode() {
  const [isDark, setIsDark] = useState(() => {
    // Check for dark class on document
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  useEffect(() => {
    // Observe changes to the dark class
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

export function AustraliaMap({ sites }: AustraliaMapProps) {
  const navigate = useNavigate();
  const isDark = useDarkMode();

  // Filter sites with valid coordinates
  const sitesWithCoords = sites.filter(
    (site) => site.latitude !== null && site.longitude !== null
  );

  // Create icons based on current theme
  const activeIcon = createMarkerIcon('#f97316', true, isDark); // brand-orange
  const inactiveIcon = createMarkerIcon('#9ca3af', false, isDark); // gray-400

  return (
    <div className="card p-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Site Locations
      </h3>

      <div className="site-map relative w-full rounded-lg overflow-hidden" style={{ height: '450px' }}>
        <MapContainer
          center={AUSTRALIA_CENTER}
          zoom={DEFAULT_ZOOM}
          maxZoom={TILE_MAX_ZOOM}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} maxZoom={TILE_MAX_ZOOM} />

          {sitesWithCoords.map((site) => (
            <Marker
              key={site.id}
              position={[site.latitude!, site.longitude!]}
              icon={site.active ? activeIcon : inactiveIcon}
              eventHandlers={{
                click: () => navigate(`/site/${site.id}`),
              }}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">{site.name}</p>
                  <p className="text-gray-600">Region: {site.region}</p>
                  <p className="text-gray-600">
                    Status: {site.active ? 'Active' : 'Inactive'}
                  </p>
                  <p className="text-gray-600">
                    Solar: {site.solar.capacity_kw.toFixed(1)} kW | Storage: {site.battery.capacity_kwh.toFixed(1)} kWh
                  </p>
                  <button
                    onClick={() => navigate(`/site/${site.id}`)}
                    className="mt-2 text-blue-600 hover:underline"
                  >
                    View Details →
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-brand-orange"></span>
          <span className="text-gray-600 dark:text-gray-400">Active Site</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-gray-400"></span>
          <span className="text-gray-600 dark:text-gray-400">Inactive Site</span>
        </div>
      </div>

      {sitesWithCoords.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No sites with location data available
        </div>
      )}
    </div>
  );
}
